import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { AuthService } from "../auth.service";
import { environment } from "src/environments/environment";
import { buildExtraNotesUrl } from "../utils/helpers";

/**
 * Deep-link bounce route for a location's Extra Notes feed.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Extra Notes feed (`/m/extra-notes/:location_id`) is a server-rendered EJS
 * page outside the Angular app. It has no Auth0 session, so it can only learn
 * who the poster is from `?user=` / `&uid=` query params -- which means a link
 * to it can't be written by hand or by an external report without hard-coding
 * somebody's identity into the URL.
 *
 * This component is the fix: external CSV reports link to the clean, identity-free
 *
 *     https://<app-host>/extra-notes/<location_id>
 *
 * which loads the Angular app (already authenticated), reads the CURRENT user's
 * identity out of the Auth0 profile, and forwards to the real feed URL with that
 * user's params filled in. So the distributed link is the same for everybody and
 * each person's posts are attributed to them.
 *
 * TIMING (the subtle part)
 * ------------------------
 * `auth.getEmployeeId()` returns a plain field that is populated ASYNCHRONOUSLY
 * inside `getUser$()`'s tap. Reading it directly in ngOnInit usually yields
 * `undefined`, which would forward with empty params -- the user could read the
 * notes but every post would 400 with "Missing employee name". So we wait on
 * `userProfile$` (a BehaviorSubject seeded with null) until it emits a real user.
 *
 * That single wait is sufficient: in `auth.service.ts` the tap assigns
 * `this.employeeID` BEFORE it calls `userProfileSubject$.next(user)`, so by the
 * time the profile emits, the employee id is guaranteed to be set.
 *
 * NOT LOGGED IN: `AuthGuard` on this route redirects to "/" before we ever get
 * here. (AuthGuard does not preserve a return URL, so a cold click lands the
 * user on /home and they click the report link a second time. Deliberate -- see
 * the Feature Requests card.)
 */
@Component({
  selector: "app-extra-notes-redirect",
  template: `
    <div class="extra-notes-redirect">
      <ng-container *ngIf="!failed">
        <mat-spinner diameter="48"></mat-spinner>
        <p>Opening Extra Notes&hellip;</p>
      </ng-container>

      <ng-container *ngIf="failed">
        <h3>Couldn't confirm your login</h3>
        <p>
          We couldn't load your user profile, so Extra Notes can't record who is
          posting. Reload this page, or open the location from the Locations grid
          and use the notes button there.
        </p>
      </ng-container>
    </div>
  `,
  styles: [
    `
      .extra-notes-redirect {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1em;
        padding: 4em 2em;
        text-align: center;
        color: #4f4f4f;
      }
      .extra-notes-redirect h3 {
        margin: 0;
        color: #d32f2f;
      }
      .extra-notes-redirect p {
        margin: 0;
        max-width: 30em;
      }
    `,
  ],
})
export class ExtraNotesRedirectComponent implements OnInit, OnDestroy {
  /** Flipped true when the profile never arrived; swaps the spinner for a dead-end message. */
  failed = false;

  /** How long to wait on the Auth0 profile before giving up. */
  private static readonly PROFILE_TIMEOUT_MS = 10000;

  private destroy$ = new Subject<void>();
  private timeoutHandle: any = null;

  constructor(private route: ActivatedRoute, private auth: AuthService) {}

  ngOnInit(): void {
    const locationId = this.route.snapshot.paramMap.get("location_id");

    // A bad/missing id would only produce a "/m/extra-notes/null" request that the
    // backend answers with a 400 anyway, so fail here where we can say something useful.
    if (!locationId || isNaN(parseInt(locationId, 10))) {
      this.failed = true;
      return;
    }

    this.timeoutHandle = setTimeout(() => {
      this.failed = true;
    }, ExtraNotesRedirectComponent.PROFILE_TIMEOUT_MS);

    this.auth.userProfile$
      .pipe(
        filter((user) => !!user),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (user) => {
          this.clearTimeoutHandle();
          if (this.failed) {
            // Timed out already and the message is on screen -- don't yank it away.
            return;
          }

          // Same derivation the rest of the app uses for `usernamee`, so a post made
          // via this route is attributed identically to one made from the grid.
          const usernamee = user.nickname
            ? user.nickname.charAt(0).toUpperCase() + user.nickname.slice(1)
            : "";
          const empId = this.auth.getEmployeeId();

          if (!usernamee) {
            // Without a name the feed rejects every post, so don't forward into a
            // page that looks fine but can't be used.
            this.failed = true;
            return;
          }

          // replace(), not assign(), so the browser Back button returns the user to
          // wherever they came from rather than re-triggering this redirect.
          window.location.replace(
            buildExtraNotesUrl(environment.baseUrl, locationId, usernamee, empId)
          );
        },
        () => {
          this.clearTimeoutHandle();
          this.failed = true;
        }
      );
  }

  ngOnDestroy(): void {
    this.clearTimeoutHandle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearTimeoutHandle(): void {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }
}
